import { useEffect } from "react";

import {
  EnumOwnerType,
  EnumWidgetEventSettingsDisplayType,
  SortFindManyListInput,
  useListManyQuery,
} from "gql/generated";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { EnumWidgetType } from "gql/generated";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getMultilingualString } from "shared/utils/getMultilingualString";
import _ from "lodash";
import { ButtonProps } from "@ttc3k/trekker";

type UseStartNewOperatorWidgetProps = {
  onClose: () => void;
  onNextClick: (
    widgetType: EnumWidgetType,
    name: string,
    listId?: string,
    eventSettings?: {
      displayType: EnumWidgetEventSettingsDisplayType;
      lookAheadMonths: number;
      eventTags: string[];
    },
  ) => void;
  setOnCloseClick: Dispatch<SetStateAction<() => void>>;
  setCancelButtonProps: Dispatch<SetStateAction<ButtonProps>>;
  setNextButtonProps: Dispatch<SetStateAction<ButtonProps>>;
};

export const useStartNewOperatorWidget = ({
  onClose,
  onNextClick,
  setOnCloseClick,
  setCancelButtonProps,
  setNextButtonProps,
}: UseStartNewOperatorWidgetProps) => {
  const { t } = useTranslation("widgets", {
    keyPrefix: "CREATE_NEW.OPERATOR_WIDGET",
  });
  const { organizationId } = useParams();

  const defaultWidgetDetails = useMemo(
    () => ({ type: EnumWidgetType.Operator, name: "", listId: "" }),
    [],
  );
  const defaultListOption = useMemo(
    () => ({
      value: "NEW_LIST",
      label: "+ " + t("CREATE_NEW_LIST"),
    }),
    [t],
  );

  const [widgetDetails, setWidgetDetails] = useState(defaultWidgetDetails);
  const [listOptions, setListOptions] = useState<
    { value: string; label: string }[]
  >([defaultListOption]);

  const { loading, error } = useListManyQuery({
    variables: {
      filter: {
        owner: {
          entityId: organizationId,
          type: EnumOwnerType.Organization,
        },
        archived: false,
      },
      limit: 10000,
      sort: SortFindManyListInput.UpdatedDesc,
    },
    onCompleted: (data) => {
      const listOptionsData = _.sortBy(
        data.listMany.map((list) => {
          return { value: list._id, label: getMultilingualString(list.name) };
        }),
        "label",
      );
      setListOptions([defaultListOption, ...listOptionsData]);
    },
  });

  const handleClose = useCallback(() => {
    setWidgetDetails(defaultWidgetDetails);
    onClose();
  }, [onClose, setWidgetDetails, defaultWidgetDetails]);

  const handleNextClick = useCallback(() => {
    onNextClick(widgetDetails.type, widgetDetails.name, widgetDetails.listId);
    setWidgetDetails(defaultWidgetDetails);
  }, [
    onNextClick,
    setWidgetDetails,
    defaultWidgetDetails,
    widgetDetails.listId,
    widgetDetails.name,
    widgetDetails.type,
  ]);

  useEffect(() => {
    setOnCloseClick(() => handleClose);
    setCancelButtonProps({ onClick: handleClose });
    setNextButtonProps((prev: ButtonProps) => ({
      ...prev,
      onClick: handleNextClick,
      disabled:
        widgetDetails.name.length < 1 || widgetDetails.listId.length < 1,
    }));
  }, [
    widgetDetails.name,
    widgetDetails.listId,
    handleNextClick,
    handleClose,
    setNextButtonProps,
    setCancelButtonProps,
    setOnCloseClick,
  ]);

  return {
    widgetDetails,
    listOptions,
    loading,
    error,
    setWidgetDetails,
  };
};
